import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Spinner, Alert, Toggle } from '../../components/UI';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    try { setSettings(await api.get('/settings')); } catch (e) { setError(e.message); }
  }

  function set(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  // Toggles persist immediately - flipping the switch IS the save, so admins
  // don't have to remember the separate "Save changes" button (a toggle that
  // silently needs a second save read as "the control doesn't work").
  async function saveKey(key, value) {
    const prev = settings ? settings[key] : undefined;
    set(key, value);
    setError(''); setSuccess('');
    try {
      await api.put('/settings', { [key]: value });
      setSuccess('Saved.');
    } catch (e) {
      set(key, prev); // revert the switch if the save failed
      setError(e.message);
    }
  }

  async function save() {
    setSaving(true);
    setError(''); setSuccess('');
    try {
      await api.put('/settings', settings);
      setSuccess('Settings saved.');
      await load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  if (!settings && !error) return <Spinner />;
  const s = settings || {};
  const enabled = (v) => v === 'enabled';

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Global settings</h1>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>

      <Alert type="danger">{error}</Alert>
      <Alert type="success">{success}</Alert>

      <div className="card mt-4">
        <h3>Exam parameters</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Default max exam time (minutes)</label>
            <input type="number" min={1} value={s.max_exam_time_minutes || ''} onChange={e => set('max_exam_time_minutes', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Passing percentage</label>
            <input type="number" min={0} max={100} value={s.passing_percentage || ''} onChange={e => set('passing_percentage', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <h3>AI validation provider</h3>
        <p>Used to score fill-in-the-blank / short-answer questions marked Validate_Type = AI. API keys and model names are configured on the server &mdash; here you only choose which provider to use.</p>

        <div className="form-group">
          <label>AI model</label>
          <select value={s.ai_provider || 'gemini'} onChange={e => set('ai_provider', e.target.value)}>
            <option value="gemini">Gemini / Gemma (Google)</option>
            {/* <option value="claude">Claude (Anthropic)</option> */}
          </select>
          <div className="hint">The API key for the selected provider must be set in the backend <code>.env</code>.</div>
        </div>

        <div className="toggle-row">
          <div>
            <div className="t-label">AI score is final</div>
            <div className="t-desc">
              {enabled(s.ai_score_final)
                ? 'AI-assigned scores publish automatically, without admin review.'
                : 'AI-assigned scores wait in the review queue for admin approval before publishing.'}
            </div>
          </div>
          <Toggle checked={enabled(s.ai_score_final)} onChange={v => saveKey('ai_score_final', v ? 'enabled' : 'disabled')} />
        </div>
      </div>

      <div className="card mt-4">
        <h3>Anti-cheating controls</h3>
        <p>Each control can be turned on or off independently for the live exam.</p>
        <div className="toggle-row">
          <div>
            <div className="t-label">Randomize question order</div>
            <div className="t-desc">Each candidate sees questions in a different order.</div>
          </div>
          <Toggle checked={enabled(s.randomize_questions)} onChange={v => saveKey('randomize_questions', v ? 'enabled' : 'disabled')} />
        </div>
        <div className="toggle-row">
          <div>
            <div className="t-label">Randomize option order</div>
            <div className="t-desc">MCQ options are shuffled per candidate.</div>
          </div>
          <Toggle checked={enabled(s.randomize_options)} onChange={v => saveKey('randomize_options', v ? 'enabled' : 'disabled')} />
        </div>
        <div className="toggle-row">
          <div>
            <div className="t-label">Allow copy-paste</div>
            <div className="t-desc">When off, copy, cut, paste, and right-click are blocked during the exam.</div>
          </div>
          <Toggle checked={enabled(s.allow_copy_paste)} onChange={v => saveKey('allow_copy_paste', v ? 'enabled' : 'disabled')} />
        </div>
        <div className="toggle-row">
          <div>
            <div className="t-label">Tab-switch detection</div>
            <div className="t-desc">Logs every time a candidate switches tabs or minimizes the browser mid-exam.</div>
          </div>
          <Toggle checked={enabled(s.tab_switch_detection)} onChange={v => saveKey('tab_switch_detection', v ? 'enabled' : 'disabled')} />
        </div>
      </div>
    </div>
  );
}
